<template>
  <div :id="`accordion-container-${headingId}`" @click="clickItem">
    <div :id="headingId" class="card-header" :class="hasPendingChanges ? 'has-pending-changes' : ''">
      <div
        class="accordion-header d-block"
        data-toggle="collapse"
        :data-target="'#' + id"
        :aria-controls="id"
        :aria-expanded="computedExpanded"
      >
        <div v-if="isDiscipline" class="row">
          <div class="col-12">
            <div class="row m-0">
              <div class="col-md-3 col-sm-12 py-0 d-flex align-items-center">
                <span
                  class="custom-header-title ml-0 d-flex align-items-center"
                  :class="[titleVariant, { 'header-title-bold': titleBold }]"
                >
                  {{ title }}
                </span>
              </div>
              <slot name="header-content" />

              <div
                v-if="$slots['action']"
                class="col-md-2 col-sm-12 py-0 d-flex justify-content-end"
                :class="actionVariant && actionStyle"
              >
                <slot name="action" />
              </div>
            </div>
          </div>
        </div>
        <div v-if="!isDiscipline" class="row">
          <div
            class="d-flex content-box"
            :class="[titleStyle, { 'hide-open-icon': !isCollapsable }]"
          >
            <span
              class="header-title justify-content-center align-self-center"
              :class="[
                titleVariant,
                { 'header-title-bold': titleBold },
                { 'title-disabled': disabled },
              ]"
            >
              {{ title }}
            </span>
          </div>
          <div
            class="content-box"
            :class="[
              contentStyle,
              { 'col-md-9': !$slots['action'] && !contentStyle },
            ]"
          >
            <div class="header-content d-block" :class="headerContentVariant">
              <slot name="header-content" />
            </div>
          </div>
          <div
            v-if="$slots['action']"
            class="content-box pt-2"
            :class="actionStyle"
          >
            <div class="action-container" :class="actionVariant">
              <slot name="action" />
            </div>
          </div>
          <div v-if="$slots['header-error']" class="header-error">
            <slot name="header-error" />
          </div>
        </div>
      </div>
    </div>
    <div
      :id="id"
      :aria-labelledby="headingId"
      :class="'collapse' + show"
      :data-parent="parentId"
    >
      <slot name="content" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'AccordionItem',
  computed: {
    show() {
      return !this.disabled && this.expanded === true ? ' show' : '';
    },
    headingId() {
      return `${this.id}Heading`;
    },
    parentId() {
      return `#${this.$parent.id}`;
    },
    computedExpanded() {
      return !this.disabled && this.expanded;
    },
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    expanded: {
      type: Boolean,
      default: () => false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    titleVariant: {
      type: [String, Object],
      default: () => '',
    },
    headerContentVariant: {
      type: String,
      default: () => '',
    },
    headerError: {
      type: String,
      default: () => '',
    },
    actionVariant: {
      type: String,
      default: () => '',
    },
    titleBold: {
      type: Boolean,
      default: () => true,
    },
    preventExpandId: {
      type: String,
      default: () => '',
    },
    isCollapsable: {
      type: Boolean,
      default: () => true,
    },
    titleStyle: {
      type: String,
      default: () => 'col-md-8',
    },
    contentStyle: {
      type: String,
      default: () => 'col-md-2',
    },
    actionStyle: {
      type: String,
      default: () => 'col-md-2',
    },
    isDiscipline: {
      type: Boolean,
      default: () => false,
    },
    hasPendingChanges:{
      type: Boolean,
      default: () => false,
    }
  },
  methods: {
    clickItem: function(evt) {
      let goAhead = true;
      if (this.preventExpandId != '') {
        goAhead = evt.target.id.indexOf(this.preventExpandId) == -1;
      }
      if (!goAhead || this.disabled || !this.isCollapsable) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        return false;
      }
      if (evt.target.type != 'button' && goAhead) {
        this.$emit('clickItem', { id: this.id, expanded: this.expanded });
      }
    },
  },
};
</script>

<style scoped>
.card-header {
  padding: 0px !important;
  border-bottom-color: #fff;
  border-bottom-width: 2px;
  cursor: pointer;
  min-height: 68px;
}
.card-header .accordion-header {
  display: flex;
  flex-direction: row;
  flex: 1;
  padding: 15px;
}

.card-header .accordion-header .header-title {
  letter-spacing: 0;
  color: #11291b;
  opacity: 1;
  margin-left: 30px;
  font-size: 17px;
  width: 100%;
}

.card-header .accordion-header[aria-expanded='false'] {
  background: #f2f2f2 0% 0% no-repeat padding-box;
  opacity: 1;
}

.card-header .accordion-header[aria-expanded='true'] {
  background: #e2e2e2 0% 0% no-repeat padding-box;
  opacity: 1;
}

.has-pending-changes {
  background: #EBFAF2 !important;
}

.has-pending-changes .accordion-header[aria-expanded='true']{
  background: #EBFAF2 !important;
}

.has-pending-changes .accordion-header[aria-expanded='false']{
  background: #EBFAF2 !important;
}

.title-disabled {
  color: rgb(84, 88, 92) !important;
}

.card-header .accordion-header .header-title:before {
  font-family: 'Font Awesome 5 Pro';
  content: '\f138';
  font-size: 26px;
  font-weight: 900;
  color: #3f5b60;
  opacity: 1;
  width: 20px;
  margin-right: 5%;
}

.card-header .accordion-header .custom-header-title:before {
  font-family: 'Font Awesome 5 Pro';
  content: '\f138';
  font-size: 26px;
  font-weight: 900;
  color: #3f5b60;
  opacity: 1;
  width: 20px;
  margin-right: 15px;
}
.card-header
  .accordion-header[aria-expanded='true']
  .custom-header-title:before {
  font-family: 'Font Awesome 5 Pro';
  content: '\f13a';
  font-size: 26px;
  font-weight: 900;
  color: #2db782;
  opacity: 1;
  width: 20px;
}
.card-header .accordion-header[aria-expanded='true'] .header-title:before {
  font-family: 'Font Awesome 5 Pro';
  content: '\f13a';
  font-size: 26px;
  font-weight: 900;
  color: #2db782;
  opacity: 1;
  width: 20px;
}

.card-header .accordion-header .hide-open-icon .header-title:before {
  display: none !important;
}

.action-container {
  position: absolute;
  right: 30px;
  margin-top: -4px;
}

.header-content {
  margin-left: 10px;
}
.header-title-bold {
  font-weight: 900 !important;
}
.header-error {
  margin-left: 70px;
}
</style>
